<template>
  <v-menu
    v-model="isMenuOpen"
    :close-on-content-click="false"
    min-width="auto"
    offset-y
    transition="scale-transition"
  >
    <template #activator="{ on }">
      <div style="display: flex; gap: 1em">
        <div>
          <v-text-field
            ref="cmpTextField"
            v-bind="attrs"
            :clearable="clearable"
            data-text-date-picker
            :dense="dense"
            :disabled="disabled"
            :error-messages="errorMessages"
            :label="label"
            readonly
            :value="displayValue"
            v-on="on"
            @blur="onBlur"
            @click:clear="
              $emit('input', null);
              resetDisplayValueOnClear();
            "
            @focus="$emit('focus')"
            @keydown.enter="on.click"
            @keydown.space="on.click"
          />
        </div>
      </div>
    </template>
    <v-date-picker
      ref="cmpDatePicker"
      v-model="localVuetifyRange"
      :allowed-dates="allowedDates"
      color="primary"
      :data-test-trigger-id="attrs['data-test-id']"
      :max="localMax"
      :min="localMin"
      no-title
      range
      @input="handleDatePickerInput"
      @keyup.enter.stop
    />
  </v-menu>
</template>

<script lang="ts">
import Component from 'vue-class-component';
import { Watch } from 'vue-property-decorator';
import { PropType, nextTick, shallowRef, watchEffect } from 'vue';
import { formatAnyDate, parseDate } from '@/utils/helpers/dates';
import { VDatePicker, VTextField } from 'vuetify/lib';

@Component({
  props: {
    value: {
      type: null as unknown as PropType<Date[]>,
      validator: (value: Date[]): boolean => {
        return value[0] instanceof Date && (value[1] instanceof Date || value[1] === null);
      },
      required: true,
    },
    label: {
      type: String,
      required: true,
    },
    min: {
      type: Date,
      required: false,
    },
    max: {
      type: Date,
      required: false,
    },
    allowedDates: {
      type: Function as PropType<(date: Date) => boolean>,
      required: false,
    },
    errorMessages: {
      type: Array, // Vue2 -> Vue3: string | string[]
      required: false,
    },
    dense: {
      type: Boolean,
    },
    clearable: {
      type: Boolean,
    },
    disabled: {
      type: Boolean,
    },
  },
  inheritAttrs: false,
  setup() {
    const isMenuOpen = shallowRef(false);
    const cmpTextField = shallowRef<null | InstanceType<typeof VTextField>>(null);
    const cmpDatePicker = shallowRef<null | InstanceType<typeof VDatePicker>>(null);

    // focus on the selected date on date picker open
    watchEffect(() => {
      if (!isMenuOpen.value) return;

      setTimeout(() => {
        const elDatePicker = cmpDatePicker.value?.$el;
        if (elDatePicker === undefined) return;
        (
          elDatePicker.querySelector<HTMLButtonElement>('.v-btn--active') ??
          elDatePicker.querySelector<HTMLButtonElement>('.v-btn--outlined')
        )?.focus();
      }, 100);
    });

    return { isMenuOpen, cmpTextField, cmpDatePicker };
  },
})
export default class TextDateRangePicker extends VTextField {
  protected readonly value!: Date[];
  protected readonly label!: string;
  protected readonly min?: Date;
  protected readonly max?: Date;
  protected readonly allowedDates?: (date: Date) => boolean;
  protected readonly errorMessages?: string | string[];
  protected readonly dense?: boolean;
  protected readonly clearable?: boolean;
  protected readonly disabled?: boolean;

  protected console = console;
  protected isMenuOpen!: boolean;
  protected cmpTextField!: null | InstanceType<typeof VTextField>;
  protected cmpDatePicker!: null | InstanceType<typeof VDatePicker>;
  protected localDateRange: string[] = [];
  protected localVuetifyRange: string[] = [];

  // $attrs doesn't include class and style (https://v2.vuejs.org/v2/api/#vm-attrs)
  protected get attrs(): Record<string, unknown> {
    return {
      ...this.$attrs,
      class: this.$vnode.data?.staticClass,
      style: this.$vnode.data?.staticStyle,
    };
  }

  protected get localMin(): string {
    return this.min ? this.formatVuetifyDate(this.min) : '';
  }

  protected get localMax(): string {
    return this.max ? this.formatVuetifyDate(this.max) : '';
  }

  protected get displayValue(): string {
    if (
      this.isMenuOpen ||
      (this.localDateRange[1] && this.localDateRange[1] !== this.localDateRange[0])
    ) {
      return this.localDateRange.join(' ~ ');
    }
    return this.localDateRange[0];
  }

  @Watch('localVuetifyRange', { deep: true, immediate: true })
  protected onValueChange(value: string[]): void {
    const dateRange = value.map((d) => parseDate(d));
    dateRange.sort((a, b) => a.getTime() - b.getTime());

    this.localDateRange = [
      this.formatDisplayDate(dateRange?.[0] ? dateRange[0] : null),
      this.formatDisplayDate(dateRange?.[1] ? dateRange[1] : null),
    ];
  }

  protected created(): void {
    this.localDateRange = [
      this.formatDisplayDate(this.value[0]),
      this.formatDisplayDate(this.value[1]),
    ];
    this.localVuetifyRange = [
      this.formatVuetifyDate(this.value[0]),
      this.formatVuetifyDate(this.value[1]),
    ];
  }

  protected handleDatePickerInput(): void {
    const dateRange = this.localVuetifyRange?.map((d) => parseDate(d));
    dateRange.sort((a, b) => a.getTime() - b.getTime());

    this.$emit('input', dateRange);

    this.cmpTextField?.$el.querySelector<HTMLInputElement>('input')?.focus();
  }

  protected formatVuetifyDate(date: Date): string {
    return formatAnyDate(date, 'yyyy-MM-dd');
  }

  protected formatDisplayDate(date: Date | null): string {
    return date == null ? '' : formatAnyDate(date, 'MMM d, y');
  }

  protected async resetDisplayValueOnClear(): Promise<void> {
    // v-text-field clear internally does late update to null
    await nextTick();
    await nextTick();

    // now it's null, let's fix the value to the intended display
    if (this.cmpTextField && 'internalValue' in this.cmpTextField) {
      this.cmpTextField.internalValue = this.formatDisplayDate(this.value[0]);
    }
  }

  protected hasParents(element: Element | null, parents: Element[]): boolean {
    while (element instanceof HTMLElement) {
      if (parents.includes(element)) return true;
      element = element.parentElement;
    }
    return false;
  }

  protected onBlur(): void {
    if (
      !this.hasParents(
        document.activeElement,
        [this.cmpTextField?.$el ?? null, this.cmpDatePicker?.$el ?? null].filter(
          Boolean
        ) as Element[]
      )
    ) {
      this.$emit('blur');
    }
  }
}
</script>

<style lang="scss" scoped></style>
